import { z } from 'zod';

/**
 * Privilegi di sistema (RBAC).
 * Sono definiti A CODICE, non a dato: un privilegio esiste solo se c'è un guard
 * o un `hasPrivilege()` che lo applica. I RUOLI (dato, tabella lookup) li
 * compongono in set assegnabili agli utenti.
 *
 * Convenzione di naming: `can_<azione>_<risorsa>`, nomi-risorsa in italiano.
 * Il set cresce PER-FEATURE: un privilegio entra qui quando la sua feature
 * scrive il guard che lo usa (per-mondo `can_read_progetti` ecc. arrivano dopo).
 */
export const Privilegio = z.enum([
  /** Scope: vedere TUTTI i progetti, non solo i propri (successore del bypass admin). */
  'can_access_all_progetti',
  /** Vista globale task, inclusi quelli senza progetto. Separato dallo scope progetti. */
  'can_read_all_task',
  'can_write_all_task',
  /**
   * Fasi riservate: importi e documenti sono permessi distinti, perché si può
   * dover lavorare i file di una fase senza vederne gli importi, e viceversa.
   */
  'can_access_valori_fasi_riservate',
  /** Comprende la generazione del verbale: il suo esito è un file della fase. */
  'can_access_file_fasi_riservate',
  /** Regole di gestione fasi sul singolo progetto, ereditate dal template. */
  'can_manage_phase_rules',
  /**
   * Correzione della tipologia pratica dopo la creazione: azione distinta
   * dall'update standard del progetto, come la correzione del codice fiscale
   * clienti/partner.
   */
  'can_correct_tipologia_pratica',
  /**
   * Eliminazione di un progetto: soft delete (la riga resta, con autore e
   * momento). Serve a ritirare una pratica aperta per errore senza distruggerne
   * l'archivio documentale, non a rimuovere lo storico di una pratica lavorata.
   */
  'can_delete_progetti',
  /**
   * Gestione utenti (staff Evolfin), granulare. NON esiste `can_read_utenti`: la
   * LETTURA di utenti e ruoli è aperta a ogni autenticato (serve per assegnare
   * task/fasi, comporre i team, la rubrica). Sotto privilegio solo le mutazioni.
   */
  'can_create_utenti',
  'can_write_utenti',
  'can_delete_utenti',
  /**
   * Definizione dei RUOLI (dato): creare/modificare/eliminare i set di privilegi.
   * Separato da `can_write_utenti` perché tocca l'escalation dei permessi.
   */
  'can_manage_ruoli',
  /**
   * Gestione anagrafiche clienti, granulare. Come per gli utenti la LETTURA
   * (lista/dettaglio) è aperta a ogni autenticato — serve per aprire progetti,
   * la rubrica, i picker — quindi non esiste `can_read_clienti`. Sotto privilegio
   * solo le mutazioni dell'anagrafica e delle sue entità collegate.
   */
  'can_create_clienti',
  'can_write_clienti',
  'can_delete_clienti',
  /**
   * Correzione del codice fiscale (chiave logica, altrimenti immutabile):
   * azione riservata e distinta dall'update standard, con motivazione
   * obbligatoria tracciata in `audit_log`. NON è un mutare-come-gli-altri-campi:
   * separato da `can_write_clienti` perché tocca l'identità del soggetto.
   */
  'can_correct_codice_fiscale_clienti',
  /**
   * Gestione anagrafiche partner, granulare — stesso pattern di Clienti: la
   * LETTURA è aperta (serve per scegliere il segnalatore dei clienti Esterno,
   * la rubrica, i picker), sotto privilegio solo le mutazioni.
   */
  'can_create_partner',
  'can_write_partner',
  'can_delete_partner',
  /** Correzione del codice fiscale, stesso pattern di Clienti. */
  'can_correct_codice_fiscale_partner',
  /**
   * Gestione dei template: creare, modificare la struttura (fasi +
   * documenti attesi) ed eliminare uno stampo. La LETTURA è aperta a ogni
   * autenticato (serve al picker di creazione progetto), quindi non esiste
   * `can_read_template`.
   */
  'can_manage_templates',
  /**
   * Eliminare anche uno slot "preesistente" (non nato da un upload libero),
   * non solo il file al suo interno — uso eccezionale come
   * `can_manage_phase_rules`.
   */
  'can_delete_file_slot',
  /**
   * Modificare i dati dell'azienda che usa il gestionale (l'intestazione dei
   * documenti generati). La LETTURA è aperta: serve a chiunque generi un
   * verbale, quindi non esiste `can_read_impostazioni`.
   */
  'can_manage_impostazioni',
  /**
   * Gestione della lista controllata dei tipi di documento cliente (lookup
   * table): creare, rinominare, riordinare, attivare/disattivare una voce.
   * Separato da `can_manage_impostazioni`: privilegio single-responsibility,
   * non c'entra con l'anagrafica azienda. La LETTURA resta aperta (serve al
   * picker "Aggiungi documento").
   */
  'can_manage_tipi_documento_cliente',
  /**
   * Gestione della lista delle forme giuridiche del cliente (lookup table).
   * Separato dai tipi documento: sono due liste diverse. La LETTURA resta
   * aperta (serve al form cliente).
   */
  'can_manage_tipologie_cliente',
  /**
   * Quando partono i promemoria di scadenze e task, per tutto lo studio.
   * Separato da `can_manage_impostazioni`: non c'entra con l'anagrafica azienda.
   */
  'can_manage_promemoria',
  /**
   * Consultare il registro attività (accessi, gestione utenti e ruoli, export,
   * eliminazioni). Solo lettura: il registro non si modifica da nessuna parte.
   */
  'can_read_audit_log',
]);
export type Privilegio = z.infer<typeof Privilegio>;

/**
 * Categorie in cui si raggruppano i privilegi nella UI (accordion dell'editor
 * ruoli e dei privilegi extra). Ordine = ordine di visualizzazione. Cresce con
 * i mondi: un nuovo gruppo si aggiunge qui una volta sola.
 */
export const CATEGORIE_PRIVILEGI = [
  { chiave: 'progetti', etichetta: 'Progetti e fasi' },
  { chiave: 'task', etichetta: 'Task' },
  { chiave: 'utenti', etichetta: 'Utenti e ruoli' },
  { chiave: 'clienti', etichetta: 'Clienti' },
  { chiave: 'partner', etichetta: 'Partner' },
  { chiave: 'template', etichetta: 'Template' },
  { chiave: 'file', etichetta: 'File' },
  { chiave: 'impostazioni', etichetta: 'Impostazioni' },
] as const;
export type CategoriaPrivilegio = (typeof CATEGORIE_PRIVILEGI)[number]['chiave'];

/**
 * FONTE UNICA di etichette, descrizioni e categoria dei privilegi, per la UI
 * (editor ruoli, privilegi extra dell'utente) e la documentazione. Vive qui,
 * accanto all'enum: un privilegio nuovo si aggiunge in un posto solo (come
 * `META_NOTIFICHE`). Nessun consumer deve ri-tradurre i codici altrove.
 */
export const META_PRIVILEGI: Record<
  Privilegio,
  { etichetta: string; descrizione: string; categoria: CategoriaPrivilegio }
> = {
  can_access_all_progetti: {
    etichetta: 'Vede tutti i progetti',
    descrizione: 'Accesso a tutti i progetti, non solo a quelli di cui è team.',
    categoria: 'progetti',
  },
  can_access_valori_fasi_riservate: {
    etichetta: 'Vede i valori delle fasi riservate',
    descrizione: 'Valore delle fasi riservate e il loro contributo al valore pratica del progetto.',
    categoria: 'progetti',
  },
  can_access_file_fasi_riservate: {
    etichetta: 'Accede ai file delle fasi riservate',
    descrizione: 'Documenti delle fasi riservate e generazione del verbale su quelle fasi.',
    categoria: 'progetti',
  },
  can_manage_phase_rules: {
    etichetta: 'Modifica le regole delle fasi',
    descrizione: 'Cambia sul singolo progetto le regole di gestione fasi ereditate dal template.',
    categoria: 'progetti',
  },
  can_correct_tipologia_pratica: {
    etichetta: 'Corregge la tipologia pratica',
    descrizione: 'Modifica la tipologia pratica di un progetto dopo la creazione, con motivazione.',
    categoria: 'progetti',
  },
  can_delete_progetti: {
    etichetta: 'Elimina progetti',
    descrizione:
      'Ritira un progetto: sparisce da liste e ricerche, ma resta a DB con chi lo ha eliminato.',
    categoria: 'progetti',
  },
  can_read_all_task: {
    etichetta: 'Legge tutti i task',
    descrizione: 'Vista globale dei task del team, inclusi quelli senza progetto.',
    categoria: 'task',
  },
  can_write_all_task: {
    etichetta: 'Gestisce tutti i task',
    descrizione: 'Crea, modifica e assegna task a chiunque nel team.',
    categoria: 'task',
  },
  can_create_utenti: {
    etichetta: 'Crea utenti',
    descrizione: 'Registra nuovi membri dello staff e invia loro l’invito di attivazione.',
    categoria: 'utenti',
  },
  can_write_utenti: {
    etichetta: 'Modifica utenti',
    descrizione: 'Aggiorna anagrafica, ruolo e privilegi, reinvita e reimposta la 2FA.',
    categoria: 'utenti',
  },
  can_delete_utenti: {
    etichetta: 'Disattiva utenti',
    descrizione: 'Disattiva (soft delete) e riattiva i membri dello staff.',
    categoria: 'utenti',
  },
  can_manage_ruoli: {
    etichetta: 'Gestisce i ruoli',
    descrizione: 'Crea, modifica ed elimina i ruoli e i loro set di privilegi.',
    categoria: 'utenti',
  },
  can_create_clienti: {
    etichetta: 'Crea clienti',
    descrizione: 'Registra nuove anagrafiche cliente.',
    categoria: 'clienti',
  },
  can_write_clienti: {
    etichetta: 'Modifica clienti',
    descrizione: 'Aggiorna anagrafica, sedi, persone, note e referenti dei clienti.',
    categoria: 'clienti',
  },
  can_delete_clienti: {
    etichetta: 'Elimina clienti',
    descrizione: 'Elimina anagrafiche cliente prive di progetti collegati.',
    categoria: 'clienti',
  },
  can_correct_codice_fiscale_clienti: {
    etichetta: 'Corregge il codice fiscale',
    descrizione: 'Corregge un codice fiscale errato (azione riservata, tracciata con motivazione).',
    categoria: 'clienti',
  },
  can_create_partner: {
    etichetta: 'Crea partner',
    descrizione: 'Registra nuove anagrafiche partner e le loro persone.',
    categoria: 'partner',
  },
  can_write_partner: {
    etichetta: 'Modifica partner',
    descrizione: 'Aggiorna anagrafica e persone dei partner.',
    categoria: 'partner',
  },
  can_delete_partner: {
    etichetta: 'Elimina partner',
    descrizione: 'Elimina anagrafiche partner prive di persone usate come segnalatori.',
    categoria: 'partner',
  },
  can_correct_codice_fiscale_partner: {
    etichetta: 'Corregge il codice fiscale',
    descrizione: 'Corregge un codice fiscale errato (azione riservata, tracciata con motivazione).',
    categoria: 'partner',
  },
  can_manage_templates: {
    etichetta: 'Gestisce i template',
    descrizione: 'Crea, modifica la struttura ed elimina gli stampi di pratica.',
    categoria: 'template',
  },
  can_delete_file_slot: {
    etichetta: 'Elimina slot preesistenti',
    descrizione: 'Elimina anche lo slot (non solo il file) quando non è nato da un upload libero.',
    categoria: 'file',
  },
  can_manage_impostazioni: {
    etichetta: 'Gestisci impostazioni azienda',
    descrizione:
      "Modifica ragione sociale, sede e recapiti dell'azienda, che intestano i documenti generati.",
    categoria: 'impostazioni',
  },
  can_manage_tipi_documento_cliente: {
    etichetta: 'Gestisce i tipi di documento cliente',
    descrizione:
      'Crea, rinomina, riordina e attiva/disattiva le voci della lista dei tipi documento.',
    categoria: 'impostazioni',
  },
  can_manage_tipologie_cliente: {
    etichetta: 'Gestisce le forme giuridiche del cliente',
    descrizione:
      'Crea, rinomina, riordina e attiva/disattiva le voci della lista delle forme giuridiche.',
    categoria: 'impostazioni',
  },
  can_manage_promemoria: {
    etichetta: 'Gestisce i promemoria',
    descrizione: 'Decide quanto prima arrivano i promemoria di scadenze e task.',
    categoria: 'impostazioni',
  },
  can_read_audit_log: {
    etichetta: 'Consulta il registro attività',
    descrizione:
      'Vede chi ha fatto accessi, cambiato utenti e ruoli, esportato dati o eliminato elementi.',
    categoria: 'impostazioni',
  },
};

/**
 * Privilegi raggruppati per categoria, nell'ordine di `CATEGORIE_PRIVILEGI` e
 * dell'enum. Derivato da `META_PRIVILEGI`: la UI itera QUESTO, senza sapere
 * quali codici esistano. Le categorie senza privilegi non compaiono.
 */
export const PRIVILEGI_PER_CATEGORIA: {
  chiave: CategoriaPrivilegio;
  etichetta: string;
  privilegi: Privilegio[];
}[] = CATEGORIE_PRIVILEGI.map((c) => ({
  chiave: c.chiave,
  etichetta: c.etichetta,
  privilegi: Privilegio.options.filter((p) => META_PRIVILEGI[p].categoria === c.chiave),
})).filter((c) => c.privilegi.length > 0);
